import React, { useMemo, useState } from 'react';
import { PackageCheck, Truck, Store } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, Table, Tr, Td, Stat, SimpleBadge } from './shared';

const today = () => new Date().toISOString().slice(0, 10);

// Live (WebSocket-driven) pickup vs delivery counters per business for the selected day.
export const LiveBusinessOrders = ({ compact = false }) => {
  const { orders, restaurants } = useApp();
  const [date, setDate] = useState(today());
  const rows = useMemo(() => {
    const map = new Map();
    orders.filter((o) => o.restaurantId && o.date === date).forEach((o) => {
      const entry = map.get(o.restaurantId) || { id: o.restaurantId, name: o.restaurant, pickup: 0, delivery: 0, pending: 0 };
      if (o.orderMode === 'pickup') entry.pickup += 1; else entry.delivery += 1;
      if (!['completed', 'cancelled'].includes(o.status)) entry.pending += 1;
      map.set(o.restaurantId, entry);
    });
    restaurants.forEach((r) => { if (!map.has(r.id) && !compact) map.set(r.id, { id: r.id, name: r.name, pickup: 0, delivery: 0, pending: 0 }); });
    return [...map.values()].sort((a, b) => (b.pickup + b.delivery) - (a.pickup + a.delivery)).filter((r) => !compact || r.pickup + r.delivery > 0);
  }, [orders, restaurants, date, compact]);
  const totals = rows.reduce((s, r) => ({ pickup: s.pickup + r.pickup, delivery: s.delivery + r.delivery, pending: s.pending + r.pending }), { pickup: 0, delivery: 0, pending: 0 });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-semibold text-gray-500">Día
          <input data-testid="live-business-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="block mt-1 h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white" />
        </label>
        <SimpleBadge tone="green">En vivo</SimpleBadge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Recoger en tienda" value={totals.pickup} icon={PackageCheck} bg="bg-[#E8F0FE]" iconBg="bg-[#D2E1FC] text-[#2F80ED]" sub={<p className="text-xs text-gray-500">pedidos del día</p>} />
        <Stat label="A domicilio" value={totals.delivery} icon={Truck} bg="bg-[#E6F7F1]" iconBg="bg-[#CDEFE0] text-[#22A45D]" sub={<p className="text-xs text-gray-500">pedidos del día</p>} />
        <Stat label="Total del día" value={totals.pickup + totals.delivery} icon={Store} bg="bg-[#FDECEC]" iconBg="bg-[#F9D4D2] text-[#EE3B36]" sub={<p className="text-xs text-gray-500">{totals.pending} en proceso</p>} />
      </div>
      <Card title="Pedidos de hoy por negocio (recoger vs domicilio)">
        <div className="p-3">
          <Table columns={['Negocio', 'Recoger en tienda', 'A domicilio', 'Total', 'En proceso']} rows={rows} emptyText="Aún no hay pedidos en esta fecha"
            renderRow={(r) => (
              <Tr key={r.id} data-testid={`live-business-${r.id}`}>
                <Td className="font-semibold">{r.name}</Td>
                <Td data-testid={`live-pickup-${r.id}`} className="font-extrabold text-[#2F80ED]">{r.pickup}</Td>
                <Td data-testid={`live-delivery-${r.id}`} className="font-extrabold text-[#22A45D]">{r.delivery}</Td>
                <Td className="font-semibold">{r.pickup + r.delivery}</Td>
                <Td className="text-gray-600">{r.pending}</Td>
              </Tr>
            )} />
        </div>
      </Card>
    </div>
  );
};

export default LiveBusinessOrders;
